import { ChangeEvent, FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { Button, Loading } from '../..';
import accountProvider from '../../../services/accountProvider';
import axios from 'axios';
import { AlertContext } from '../../../contexts/AlertContext';
import i18nNamespaces from '../../../const/i18nNamespaces';
import { useTranslation } from 'react-i18next';
import { selectAuthStatus, verifySMSCode } from '../../../store/auth';
import { useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../hooks/customReduxHooks';
import { Box, Link, TextField, Typography } from '@material-ui/core';
import { accountApi } from '../../../api/account/accountApi';
import useStyles from './../../../pages/PhoneVerificationPage/PhoneVerificationPageStyles';
import { parseVerifyCodeError } from '../../../utils/error';

type PhoneVerificationWithTokenInQueryProps = {
    email?: string;
};

const TIME_TO_NEXT_CODE_SEND = 30000;


type SendCodeAgainProps = {
    mt?: number | string;
    mb?: number | string;
    onCallWithCode?: () => void;
    remainingTime: number | null;
    smsAttemptsCount: number;
    callAttemptsCount: number;
    onSendCodeAgain?: () => void;
};
const SendCodeAgain = ({mt, mb, remainingTime, smsAttemptsCount, callAttemptsCount, onCallWithCode, onSendCodeAgain}: SendCodeAgainProps) => {
    const {t} = useTranslation(i18nNamespaces.AUTH);
    const isCallWithCodeUnlocked = smsAttemptsCount >= 2 && remainingTime === 0 && callAttemptsCount === 0;
   

    if(callAttemptsCount > 0){
        return null;
    }

    return (
        <Box mt={mt} mb={mb}>
            {t('codeNotReceived')}{' '}
            {remainingTime > 0 ? (
                t('resendIn', {seconds: remainingTime / 1000})
            ) : (
                <Link
                    color={'secondary'}
                    style={{cursor: 'pointer'}}
                    onClick={isCallWithCodeUnlocked ? onCallWithCode : onSendCodeAgain}
                >
                    {isCallWithCodeUnlocked ? t('callWithCode') : t('sendAgain')}
                </Link>
            )}
        </Box>
    );
};



const PhoneVerificationWithTokenInQuery = ({email} : PhoneVerificationWithTokenInQueryProps) => {
    const {t} = useTranslation(i18nNamespaces.AUTH);
    const {t: errorT} = useTranslation(i18nNamespaces.ERRORS);
    const [loading, setLoading] = useState(false);
    const [firstCodeSent, setFirstCodeSent] = useState(false);
    const [error, setError] = useState<string>('');
    const dispatch = useAppDispatch();
    const openAlert = useContext(AlertContext);
    const [code, setCode] = useState<string>('');
    const [calledWithCode, setCalledWithCode] = useState(false);
    const status = useAppSelector(selectAuthStatus);

    const [remainingTime, setRemainingTime] = useState<number | null>(null);
    const [smsAttemptsCount, setSmsAttemptsCount] = useState(0);
    const [callAttemptsCount, setCallAttemptsCount] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const classes = useStyles();

     useEffect(() => {
        if (remainingTime === null) return;
        if (remainingTime <= 0) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            setRemainingTime(0);
        }
    }, [remainingTime]);

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    const startCountdown = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        setRemainingTime(TIME_TO_NEXT_CODE_SEND);
        intervalRef.current = setInterval(() => {
            setRemainingTime(prev => (prev !== null ? prev - 1000 : 0));
        }, 1000);
    };

    const handleVerifyCodeError = (err: unknown) => {
        if (axios.isAxiosError(err)){
            if(err?.response?.status === 401){
                return openAlert({
                    severity: 'error',
                    message: errorT("sessionExpired"),
                });
            }
            const error = err.response?.data as string[];
            openAlert({
                severity: 'error',
                message: parseVerifyCodeError(error, errorT),
            });
        }
    };

    const handleSendCodeAgain = async () => {
        try {
            startCountdown();
            await accountProvider.sendSMSCode();
        } catch (err) {
            console.error('Send code again error', err);
            handleVerifyCodeError(err);
        } finally {
            setSmsAttemptsCount(prev => prev + 1);
        }
    };

    const handleCallWithCode = async () => {
        try {
            await accountApi.callWithSmsCode();
            setCallAttemptsCount(prev => prev + 1);
            setCalledWithCode(true);
        }catch(err){
            console.error('Call with code error', err);
            handleVerifyCodeError(err);
        }
    }


    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        dispatch(verifySMSCode({email, code}));
    };

    const onCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
        setCode(e.target.value);
        setError('');
    };

    const handleFirstCodeSent = async () => {
            try{
                setLoading(true);
                await accountProvider.sendSMSCode();
            } catch (error) {
                console.error(error);
                handleVerifyCodeError(error);
            } finally {
                setLoading(false);
                startCountdown();
                setFirstCodeSent(true);
                setSmsAttemptsCount(prev => prev + 1);
            }
        }
    
    return (
        <>
        <div className={classes.container}>
            <Loading loading={status === 'loading'} withBackdrop />
            <Box
                py={3}
                justifyContent="center"
                alignItems="center"
                display={'flex'}
                flexDirection="column"
            >
                <Typography variant={'h3'}>
                    {t('phoneNumberVerification')}
                </Typography>
                <Box component="span" pt={2} pb={4}>
                    {!firstCodeSent ? t('weWillSendYouSmsWithCode') : 
                        calledWithCode ? t('weWillCallYouWithCode') : t('smsWithVerificationCodeWasSend')
                    }
                </Box>
                {!firstCodeSent ? (
                    <Button
                    color="secondary"
                    variant="contained"
                    className={classes.button}
                    onClick={handleFirstCodeSent}
                    disabled={loading}
                >
                    {t('sendSmsCode')}
                </Button>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Box mb={1}>
                            <TextField
                                className={classes.input}
                                label={calledWithCode ? t('enterCodeFromCall') : t('enterCodeFromSMS')}
                                type="text"
                                id="secondName"
                                variant="outlined"
                                value={code}
                                onChange={onCodeChange}
                                autoComplete="off"
                                error={!!error}
                                helperText={error && error}
                                autoFocus
                            />
                        </Box>
                        <SendCodeAgain 
                            mt={0} 
                            mb={2} 
                            onCallWithCode={handleCallWithCode}
                            onSendCodeAgain={handleSendCodeAgain}
                            remainingTime={remainingTime}
                            smsAttemptsCount={smsAttemptsCount}
                            callAttemptsCount={callAttemptsCount}
                        />
                        <Button
                            color="secondary"
                            variant="contained"
                            type="submit"
                            className={classes.button}
                            disabled={!code}
                        >
                            {t('next')}
                        </Button>
                    </form>
                )}
                </Box>
            </div>
        </>
    );
};

export default PhoneVerificationWithTokenInQuery;